import { Loader, TextButton } from '@wix/design-system'
import { useContent } from '@wix/funnel-builder-lizard'
import React, { FC, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { fetchSiteInfo, SiteInfo } from '../../../api/siteInfo'
import { usePageLogger } from '../../../bi/mfi'
import { useFlowContext } from '../../../hooks/useFlowContext'
import { useNext } from '../../../hooks/useNext'
import { Button } from '../../reusable/Button'
import WixLogo from '../../reusable/icons/WixLogo.svg'
import PlusIcon from './PlusIcon.svg'
import { TReturningUserSplit as Content } from './ReturningUserSplit.content'
import { returningUserSplitDataHooks as dataHooks } from './ReturningUserSplit.dataHooks'
import s from './ReturningUserSplit.scss'

export const ReturningUserSplit: FC = () => {
  const [submittingKey, setSubmittingKey] = useState<string | null>(null)
  const [siteInfo, setSiteInfo] = useState<SiteInfo>()
  const { content } = useContent<Content>()
  const [flowContext] = useFlowContext()
  const [searchParams] = useSearchParams()
  const next = useNext()
  const pageLogger = usePageLogger()

  const { question, secondaryQuestion } = content
  const { questionKey, content: questionContent, options } = question

  const hasExistingSite = options.some(o => o.existingSite)
  const [isLoadingSiteInfo, setIsLoadingSiteInfo] = useState(hasExistingSite)

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    // TODO: remove query param fallback once testing is complete
    const metaSiteId = flowContext?.metadata?.metaSiteId ?? searchParams.get('metaSiteId')
    if (!metaSiteId || !hasExistingSite) {
      setIsLoadingSiteInfo(false)
      return
    }
    fetchSiteInfo(metaSiteId).then(res => {
      setSiteInfo(res.data)
      setIsLoadingSiteInfo(false)
    })
  }, [flowContext, searchParams, options])

  const handleOptionClick = (optionKey: string) => {
    if (submittingKey !== null) {
      return
    }
    setSubmittingKey(optionKey)
    void pageLogger.answer({ answer: optionKey })
    next({ [questionKey]: { values: [optionKey] } })
  }

  const handleSecondaryOptionClick = (optionKey: string) => {
    if (submittingKey !== null) {
      return
    }
    setSubmittingKey(optionKey)
    void pageLogger.answer({ answer: optionKey })
    next({ [secondaryQuestion.questionKey]: { values: [optionKey] } })
  }

  if (isLoadingSiteInfo) {
    return (
      <div className={s.container}>
        <WixLogo className={s.logo} />
        <Loader size="medium" />
      </div>
    )
  }

  return (
    <div className={s.container}>
      <WixLogo className={s.logo} />
      <div className={s.header}>
        <p className={s.subtitle} data-hook={dataHooks.subtitle()}>
          {questionContent.subtitle}
        </p>
        <h1 className={s.title} data-hook={dataHooks.title()}>
          {questionContent.title}
        </h1>
      </div>
      <div className={s.cards}>
        {options.map(option => {
          const label =
            option.existingSite && siteInfo && !siteInfo.isDefaultSiteName
              ? `Continue with ${siteInfo.siteName}`
              : option.label
          const description = option.existingSite && siteInfo?.publicUrl ? siteInfo.publicUrl : option.description
          const thumbnail = option.existingSite ? siteInfo?.thumbnailUrl : option.img.content.src

          return (
            <div key={option.optionKey} className={s.card}>
              {option.existingSite ? (
                <div className={s.continueCardVisual}>
                  <div className={s.browserFrame}>
                    <div className={s.browserBar}>
                      <span className={s.browserDot} />
                      <span className={s.browserDot} />
                      <span className={s.browserDot} />
                    </div>
                    <div className={s.browserContent}>
                      <div className={s.thumbnailClip}>
                        {thumbnail ? (
                          <img src={thumbnail} alt={option.img.content.alt} className={s.thumbnail} />
                        ) : (
                          <div className={s.thumbnailPlaceholder} />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className={s.createCardVisual}>
                  {thumbnail ? (
                    <img src={thumbnail} alt={option.img.content.alt} className={s.thumbnail} />
                  ) : (
                    <PlusIcon className={s.plusIconSvg} />
                  )}
                </div>
              )}
              <div className={s.cardContent}>
                <div className={s.cardTexts}>
                  <h2 className={s.cardTitle} data-hook={dataHooks.optionTitle({ optionKey: option.optionKey })}>
                    {label}
                  </h2>
                  <p className={option.existingSite ? s.cardUrl : s.cardDescription}>{description}</p>
                </div>
                <Button
                  isLoading={submittingKey === option.optionKey}
                  onClick={() => handleOptionClick(option.optionKey)}
                  label={option.btn}
                  dataHook={dataHooks.optionCta({ optionKey: option.optionKey })}
                  size="medium"
                  skin="standard"
                  className={s.cardButton}
                />
              </div>
            </div>
          )
        })}
      </div>
      {secondaryQuestion.options.map(option => (
        <TextButton
          key={option.optionKey}
          skin="dark"
          underline="always"
          onClick={() => handleSecondaryOptionClick(option.optionKey)}
          dataHook={dataHooks.secondaryOptionCta({ optionKey: option.optionKey })}
          className={s.mySitesLink}
          disabled={submittingKey !== null}
        >
          {option.label}
        </TextButton>
      ))}
    </div>
  )
}
